import React from 'react';
import { SystemText } from '@salesperformanceint/e4shared';
import { Link } from 'react-router-dom';
import Feedback from 'components/asideRight/components/feedback';
import { useDispatch, useSelector } from 'react-redux';
import { loadPrismMenuStats, setPrismMenu, toggleLeftAside } from 'actions/ui';
import { hasModule } from 'helpers/profile';
import Signout from 'components/general/signout';
import BlobImage from 'components/blobImage';
import { posthog } from 'posthog-js';

export const Menu = ({ history }) => {
    const dispatch = useDispatch();
    const [showFeedback, setShowFeedback] = React.useState(false);
    const { user } = useSelector(state => state.user);
    const active = useSelector(state => state.ui.prismMenu);
    const stats = useSelector(state => state.ui.prismMenuStats);
    const manager = user.staff > 0;
    const admin = user.administrator;
    const systemConfig = user.sys_Config;

    const trackEvent = trackingName => {
        dispatch(setPrismMenu(trackingName));
        posthog.capture('left-menu-click', { menuItem: trackingName }, { transport: 'sendBeacon' });
    };

    React.useEffect(() => {
        dispatch(loadPrismMenuStats());
    }, [user?.id]);

    return (
        <>
            <aside className="sidebar">
                {/* HOME + COLLAPSE BUTTON */}
                <div className="sidebar__title-row">
                    <span className="sidebar__title">
                        <img src="/dist/img/richardson_sp.png" alt="Richardson" />
                    </span>
                    {/* Desktop collapse button */}
                    <label
                        htmlFor="collapse-toggle"
                        className="collapse-btn desktop-collapse"
                        onClick={() => dispatch(toggleLeftAside())}
                    >
                        <i className="fa fa-sidebar"></i>
                    </label>
                    {/* Mobile close button */}
                    <label
                        htmlFor="mobile-toggle"
                        className="collapse-btn mobile-close"
                        onClick={() => dispatch(toggleLeftAside())}
                    >
                        <i className="fa fa-times"></i>
                    </label>
                </div>

                {/* NAVIGATION */}
                <ul className="sidebar__nav">
                    {user.companySettings?.prism_UI && (user?.partner_Id > 0 || user?.spoofing) && (
                        <li>
                            <div className="spoof-menu-option">
                                <Link
                                    className={`sidebar__link ${active === 'Spoof' ? 'active' : ''} spoof-menu-option`}
                                    to="/spoof/users"
                                    onClick={() => trackEvent('Spoof')}
                                >
                                    <i className="fa fa-users"></i>
                                    <span>
                                        {!user.spoofing && SystemText('SideMenu/Spoof')}
                                        {user.spoofing && SystemText('SideMenu/Spoof/Spoofing')}
                                    </span>
                                </Link>
                            </div>
                        </li>
                    )}
                    <li>
                        <Link
                            className={`sidebar__link ${active === 'Home' ? 'active' : ''}`}
                            to="/"
                            onClick={() => trackEvent('Home')}
                        >
                            <i className="fa fa-home"></i>
                            <span>{SystemText('SideMenu/Home')}</span>
                        </Link>
                    </li>
                    <li>
                        <Link
                            className={`sidebar__link ${active === 'Reviews' ? 'active' : ''}`}
                            to="/develop/reviews"
                            onClick={() => trackEvent('Reviews')}
                        >
                            <i className="fa fa-gauge"></i>
                            <span>{SystemText('SideMenu/Reviews')}</span>
                            {stats.reviews_Count > 0 && <span className="badge">{stats.reviews_Count}</span>}
                        </Link>
                    </li>
                    {hasModule(user, 'pathways') && (
                        <li>
                            <Link
                                className={`sidebar__link ${active === 'Pathways' ? 'active' : ''}`}
                                to="/pathways"
                                onClick={() => trackEvent('Pathways')}
                            >
                                <i className="fa fa-route"></i>
                                <span>{SystemText('SideMenu/Pathways')}</span>
                                {stats.pathways_Count > 0 && <span className="badge">{stats.pathways_Count}</span>}
                            </Link>
                        </li>
                    )}                    
                    <li>
                        <Link
                            className={`sidebar__link ${active === 'Library' ? 'active' : ''}`}
                            to="/library"
                            onClick={() => trackEvent('Library')}
                        >
                            <i className="fa fa-book-open"></i>
                            <span>{SystemText('SideMenu/Library')}</span>
                        </Link>
                    </li>
                    
                    {user.hasRolePlayAiAccess && (
                    <li>
                        <Link
                            className={`sidebar__link ${active === 'CourseLibraryRolePlay' ? 'active' : ''}`}
                            to="/course-library-roleplay"
                            onClick={() => trackEvent('CourseLibraryRolePlay')}
                        >
                            <i className="fa fa-sparkles"></i>
                            <span>{SystemText('SideMenu/CourseLibraryRolePlay')}</span>
                        </Link>
                    </li>
                    )}
                    {hasModule(user, 'objectives') && (
                        <li>
                            <Link
                                className={`sidebar__link ${active === 'Objectives' ? 'active' : ''}`}
                                to="/objectives"
                                onClick={() => trackEvent('Objectives')}
                            >
                                <i className="fa fa-circle-check"></i>
                                <span>{SystemText('SideMenu/Objectives')}</span>
                                {stats.objectives_Count > 0 && <span className="badge">{stats.objectives_Count}</span>}
                            </Link>
                        </li>
                    )}
                    {hasModule(user, 'knowledge') && (
                        <li>
                            <Link
                                className={`sidebar__link ${active === 'KnowledgeBank' ? 'active' : ''}`}
                                to="/knowledge-bank"
                                onClick={() => trackEvent('KnowledgeBank')}
                            >
                                <i className="fa fa-comment"></i>
                                <span>{SystemText('SideMenu/Discussions')}</span>
                            </Link>
                        </li>
                    )}

                    {hasModule(user, 'objectives') && (
                        <li>
                            <Link
                                className={`sidebar__link ${active === 'Practice' ? 'active' : ''}`}
                                to="/pitchback"
                                onClick={() => trackEvent('Practice')}
                            >
                                <i className="fa fa-video-camera"></i>
                                <span>{SystemText('SideMenu/Practice')}</span>
                            </Link>
                        </li>
                    )}
                    {hasModule(user, 'blogs') && (
                        <li>
                            <Link
                                className={`sidebar__link ${active === 'Blogs' ? 'active' : ''}`}
                                to="/blog"
                                onClick={() => trackEvent('Blogs')}
                            >
                                <i className="fa fa-comments"></i>
                                <span>{SystemText('SideMenu/Blogs')}</span>
                            </Link>
                        </li>
                    )}

                    {(manager || admin || systemConfig) && (
                        <>
                            <li>
                                <hr />
                            </li>
                            {manager && (
                                <li>
                                    <Link
                                        className={`sidebar__link ${active === 'MyTeam' || active === 'Team' ? 'active' : ''}`}
                                        to="/my-team"
                                        //to={`/search?manager=${user.id}&name=${user.full_Name}`}
                                        onClick={() => trackEvent('MyTeam')}
                                    >
                                        <i className="fa fa-users"></i>
                                        <span>{SystemText('SideMenu/MyTeam')}</span>
                                        {stats.manager_Action_Count > 0 && (
                                            <span className="badge">{stats.manager_Action_Count}</span>
                                        )}
                                    </Link>
                                </li>
                            )}
                            {(manager || admin) && (
                                <>
                                    <li>
                                        <Link
                                            className={`sidebar__link ${active === 'Insights' ? 'active' : ''}`}
                                            to="/insights"
                                            onClick={() => trackEvent('Insights')}
                                        >
                                            <i className="fa fa-chart-pie"></i>
                                            <span>{SystemText('SideMenu/TeamInsights')}</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <hr />
                                    </li>

                                    <li>
                                        <Link
                                            className={`sidebar__link ${active === 'Organisation' ? 'active' : ''}`}
                                            to="/organization"
                                            onClick={() => trackEvent('Organisation')}
                                        >
                                            <i className="fa fa-sitemap"></i>
                                            <span>{SystemText('SideMenu/Organisation')}</span>
                                        </Link>
                                    </li>
                                </>
                            )}
                            {(admin || systemConfig) && (
                                <li>
                                    <Link
                                        className={`sidebar__link ${active === 'Admin' ? 'active' : ''}`}
                                        to="/admin"
                                        onClick={() => trackEvent('Admin')}
                                    >
                                        <i className="fa fa-cog"></i>
                                        <span>{SystemText('SideMenu/Settings')}</span>
                                    </Link>
                                </li>
                            )}
                        </>
                    )}
                    <li className="spacer"></li>
                    <li>
                        <button
                            className={`sidebar__link ${active === 'home' ? 'active' : ''}`}
                            onClick={() => setShowFeedback(true)}
                        >
                            <i className="fa fa-question-circle"></i>
                            <span>{SystemText('SideMenu/Support')}</span>
                        </button>
                    </li>
                </ul>

                <div className="sidebar__spacer"></div>

                {/* USER BLOCK */}
                <div className="sidebar__user">
                    <label htmlFor="signout-toggle">
                        <div className="user-block">
                            <div className="avatar">
                                <BlobImage
                                    folder="avatar"
                                    src={user.avatar}
                                    initials={user.initials}
                                    className="img-circle"
                                    width="40"
                                    height="40"
                                />
                            </div>
                            <div className="user-info">
                                <strong>{user.full_Name}</strong>
                                <small style={{ color: 'var(--text-muted)' }}>{user.email}</small>
                            </div>
                            <i className="fa fa-arrow-right-from-bracket ml5 mr5" />
                        </div>
                    </label>
                </div>
                <Feedback externalShow={showFeedback} externalHide={() => setShowFeedback(false)} />
            </aside>
            <ul className="signout-panel">
                <Signout history={history} />
            </ul>
        </>
    );
};

export default Menu;
